import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import ConfirmDenyDialog from '../../../components/shared/ModalNew/ConfirmDenyDialog';
import Icon from '../../../components/shared/Icon';
import NoResultsInfoMessage from '../../../components/shared/InfoMessage/NoSearchResults';
import PermissionLinkWrapper from '../../../components/shared/PermissionLinkWrapper';
import SeeMoreLink from '../../../components/shared/SeeMoreLink';

import { ROUTES } from '../../../constants';
import { useModal } from '../../../context/modal-context';
import { usePermission } from '../../../hooks/usePermission';
import { PermissionsEnum } from '../../../types';
import { formatDateWithPoints } from '../../../utils';

/** Fraud Cases List and Skeleton reusable class names */
const FRAUD_CASES_CLASS_NAME = {
  holder: 'flex flex-col gap-3',
  item: 'pks-card-bg overflow-hidden rounded-lg',
  itemContent:
    'pks-layout-col justify-center bg-white ml-[6px] pl-[22px] pr-4 py-3',
};

/** Interface for FraudCasesList component props */
interface FraudCasesListProps {
  data: any[]; // Array of fraud case objects
  preview?: boolean; // Optional flag to show "See More" link
  onDelete?: (id: number) => void;
}

/**
 * Fraud Cases List Component
 * Displays a list of fraud cases with links to detailed views and an optional "See More" link.
 *
 * @param {Object} props - Component props
 * @param {FraudCaseInterface[]} props.data - List of fraud cases to display.
 * @param {boolean} [props.preview=true] - Whether to show the "See More" link.
 */
const FraudCasesList = ({
  data,
  preview = true,
  onDelete,
}: FraudCasesListProps) => {
  const location = useLocation();
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();

  const isFraudCasesPage = location.pathname.includes('/fraud-cases');

  /* Permissions */
  const canViewFraudCaseData = usePermission([PermissionsEnum.FraudcaseData]);

  const handleOpenDeleteFraudCaseModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number
  ) => {
    if (!onDelete) return;

    e.preventDefault();
    e.stopPropagation();

    openModal(
      <ConfirmDenyDialog
        infoMessage={t('fraud_cases.prompt_fraud_case_delete')}
        onConfirm={() => onDelete(id)}
        onDeny={closeModal}
        closeModal={closeModal}
      />,
      t('fraud_cases.title_fraud_case_delete_preview')
    );
  };

  return (
    <>
      <ul className={FRAUD_CASES_CLASS_NAME.holder}>
        {data?.length === 0 ? (
          <li>
            <NoResultsInfoMessage />
          </li>
        ) : (
          data.map((item) => {
            const formatedDate = formatDateWithPoints(
              item?.date_edited?.date || item?.date_created?.date
            );
            return (
              <li
                key={item.id}
                className={`${FRAUD_CASES_CLASS_NAME.item} ${
                  canViewFraudCaseData ? 'pks-card-bg-hover' : ''
                }`}
              >
                <PermissionLinkWrapper
                  canPreview={canViewFraudCaseData}
                  linkProps={{
                    to: `${ROUTES.DASHBOARD.FRAUD_CASES.ROOT.absPath}/${item.id}`,
                    state: {
                      backTo: isFraudCasesPage
                        ? `${location.pathname}${location.search}`
                        : '../',
                    },
                    className:
                      'flex gap-4 items-center justify-between ml-[6px] pl-[22px] pr-4 py-3 bg-white',
                  }}
                >
                  <div className="pks-layout-col">
                    <p className="font-bold text-lg">{item.title}</p>
                    {item?.user_created?.isDeleted !== 1 &&
                      item?.user_created?.firstName &&
                      item?.user_created?.lastName && (
                        <p className="text-secondary-400">
                          {item.user_created.firstName +
                            ' ' +
                            item.user_created.lastName}
                        </p>
                      )}
                    {item?.user_created?.organization?.name && (
                      <p className="text-secondary-400">
                        {item.user_created.organization.name}
                      </p>
                    )}
                    <span className="text-secondary-400">{formatedDate}</span>
                  </div>
                  {onDelete && (
                    <button
                      aria-label={t('fraud_cases.delete_fraud_case')}
                      onClick={(e) =>
                        handleOpenDeleteFraudCaseModal(e, item.id)
                      }
                    >
                      <Icon
                        name="trash"
                        aria-hidden="true"
                        className="text-white"
                        tabIndex={-1}
                      />
                    </button>
                  )}
                </PermissionLinkWrapper>
              </li>
            );
          })
        )}
      </ul>

      {preview && (
        <SeeMoreLink url="fraud-cases" label={t('fraud_cases.link')} />
      )}
    </>
  );
};

export default FraudCasesList;

/**
 * Fraud Cases List Skeleton Component
 * Displays skeletons for the fraud cases list while data is loading.
 *
 * @param {Object} props - Component props
 * @param {number} [props.count=4] - Number of skeleton items to display.
 * @returns {React.JSX.Element} - The rendered skeleton component.
 */
export const FraudCasesListSkeleton = ({
  count = 10,
  rows = 3,
  minHeight = 126,
}: {
  count?: number;
  rows?: number;
  minHeight?: number;
}): React.JSX.Element => {
  const skeletonItems = Array.from({ length: count }, (_, index) => (
    <li key={index} className={`${FRAUD_CASES_CLASS_NAME.item} animate-pulse`}>
      <div
        className={`${FRAUD_CASES_CLASS_NAME.itemContent} space-y-2`}
        style={{
          minHeight: minHeight,
        }}
      >
        {Array.from({ length: rows }, (_, i) => (
          <p
            key={i}
            className="h-4 bg-gray-300 rounded"
            style={{
              width: `${Math.random() * 50 + 50}%`, // Random width between 50% and 100%
            }}
          ></p>
        ))}
      </div>
    </li>
  ));

  return <ul className={FRAUD_CASES_CLASS_NAME.holder}>{skeletonItems}</ul>;
};
